/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file index.jsx
 * @description Profiles Edit/View Page.
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import FormGenerator from '@nephro/components/FormGenerator';

// Helpers
import {
  getLastItemUrl,
  bottomOptionsCreateEdit,
  bottomOptionsRead,
  formItemNameDesc
} from '@nephro/commons/helpers/utils';

// Styles
import styles from './styles.module.css';

let profileData = {};

/**
 * Profiles View/Edit
 * @param { Object } profileActions - Profile Actions.
 * @param { Object } actions - App Actions.
 * @return { Node } React node.
 */
const ProfilesViewEdit = ({ profileActions, actions }) => {
  const history = useHistory();
  const location = useLocation();
  const { t: translate } = useTranslation();

  const isView = location.pathname.includes('view');

  const [isReadOnly] = React.useState(isView);
  const [profileInfo, setProfileInfo] = React.useState(null);
  const [profileId] = React.useState(
    parseInt(getLastItemUrl(location.pathname), 10)
  );

  const { getProfileById, updateProfile } = profileActions;
  const { setLoading } = actions;

  const onGoBack = () => {
    history.goBack();
  };

  // Function to get and set profile info
  const onGetProfileInfo = (_, response) => {
    if (response.data) {
      const { description, name } = response.data;
      profileData = { description, name };
      setProfileInfo({ description, name }, setLoading(false));
    } else {
      onGoBack();
    }
  };

  React.useEffect(() => {
    getProfileById({ id: profileId }, onGetProfileInfo);
  }, []);

  // Function to get on submit form values
  const onFinish = (values) => {
    updateProfile({ ...values, id: profileId }, onGoBack);
  };

  return (
    <div className="flex flex-col h-full px-7 pt-5 pb-7 ">
      {profileInfo && (
        <FormGenerator
          initialValues={profileData}
          onFinish={onFinish}
          items={formItemNameDesc(translate, isReadOnly)}
          bottomOptions={
            !isReadOnly
              ? bottomOptionsCreateEdit(
                  onGoBack,
                  translate,
                  'edit',
                  styles.actionButton
                )
              : bottomOptionsRead(onGoBack, translate, styles.actionButton)
          }
        />
      )}
    </div>
  );
};

/**
 * Component definition of proptypes.
 */
ProfilesViewEdit.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  profileActions: PropTypes.shape({
    createProfile: PropTypes.func.isRequired,
    getProfileById: PropTypes.func.isRequired,
    updateProfile: PropTypes.func.isRequired
  }).isRequired
};

export default ProfilesViewEdit;
